import { ErrorBoundary } from "react-error-boundary";
import { Suspense, useState, useEffect, useRef } from "react";
import { router } from "@inertiajs/react";
import "./applications.scss";
import useDevice from "@/hooks/use_device";
import ApplicationsList from "@/components/applications/applications_list";
import SelectedApplication from "@/components/applications/selected_application";
import { Row } from "@sprout/layout";
import Application from "@/types/application";
import { Error } from "@components/error";
import useApplications from "@/hooks/use_applications";
import LoadingSpinner from "@/sprout/loading_spinner";
import EmptyState from "@components/empty_state.tsx";
import Fingerprint1 from "@images/fingerprint_1.svg";
import useTranslation from "@hooks/use_translation.ts";
import { IconButton } from "@sprout/icon_button.tsx";
import BackIcon from "@sprout/icons/back.tsx";
import useTemporaryStorage from "@/hooks/use_temporary_storage";
import { useDebounce } from "react-use";

type DeferredPageProps = {
  applications?: Application[];
};

type Props = DeferredPageProps & {
  selected_application_id?: number;
  tokens_remaining: number;
  dream_job_tokens: number;
};

const Applications = (props: Props) => {
  const { selected_application_id: selectedApplicationId, dream_job_tokens } = props;
  const [selectedApplication, setSelectedApplication] = useState<Application | undefined>(
    undefined,
  );
  const [tab, setTab] = useTemporaryStorage("application-tab", "active");
  const [search, setSearch] = useTemporaryStorage("application-search", "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const { isLoading, data } = useApplications();
  const device = useDevice();
  const { t } = useTranslation("applications");

  const active = data.active || { applications: [] };
  const inactive = data.inactive || { applications: [] };

  const filterApplications = (applicationsToFilter: Application[]) => {
    return applicationsToFilter.filter((application) => {
      return (
        application.job_title.toLowerCase().includes(search.toLowerCase()) ||
        application.company_name.toLowerCase().includes(search.toLowerCase())
      );
    });
  };

  const filteredActive = filterApplications(active.applications || []);
  const filteredInactive = filterApplications(inactive.applications || []);

  useDebounce(
    () => {
      setSearch(debouncedSearch);
    },
    200,
    [debouncedSearch],
  );

  // Modified route function with check to prevent unnecessary navigation
  const navigateToApplication = (applicationId?: number | null) => {
    // Don't navigate if the ID is already the same
    if (applicationId === selectedApplicationId) {
      return;
    }

    const route = applicationId ? `/applications/${applicationId}` : "/applications";
    router.visit(route, { only: ["selected_application_id"] });
  };

  const prevTabRef = useRef<string | null>(null);

  // Default to the first application in the tab when the tab value changes.
  useEffect(() => {
    // Skip if it's not actually a tab change (prevents running on other dependency changes)
    if (prevTabRef.current === tab) return;

    prevTabRef.current = tab;

    if (device.type == "web" && data) {
      const currentList = tab === "active" ? filteredActive : filteredInactive;

      const currentSelectionInTab =
        selectedApplicationId && currentList.find((app) => app.id === selectedApplicationId);

      // Only select the first item if there's no valid selection in the current tab
      if (currentList.length > 0 && !currentSelectionInTab) {
        navigateToApplication(currentList[0].id);
      } else if (currentList.length === 0 && selectedApplicationId) {
        navigateToApplication(null);
      }
    }
  }, [tab, data, device.type, selectedApplicationId]);

  // Update the selected application when the selectedApplicationId changes
  useEffect(() => {
    if (data && selectedApplicationId) {
      const currentList = tab === "active" ? filteredActive : filteredInactive;
      const appInCurrentTab = currentList.find((app) => app.id === selectedApplicationId);

      if (appInCurrentTab) {
        setSelectedApplication(appInCurrentTab);
      }
    } else if (!selectedApplicationId) {
      setSelectedApplication(undefined);
    }
  }, [selectedApplicationId, data, tab]);

  const showApplicationsList = device.type == "web" || !selectedApplication;
  const showSelectedApplication = device.type == "web" || selectedApplication;

  const onViewParentApplication = async (applicationId: number) => {
    const parentApplication =
      filteredActive.find((app) => app.id === applicationId) ||
      filteredInactive.find((app) => app.id === applicationId);

    if (parentApplication) {
      setSelectedApplication(parentApplication);
      await setTab(
        filteredActive.map((app) => app.id).includes(parentApplication.id) ? "active" : "inactive",
      );
      await navigateToApplication(applicationId);
    }
  };

  return (
    <>
      {showApplicationsList && (
        <ApplicationsList
          activeApplications={filteredActive}
          inactiveApplications={filteredInactive}
          isLoading={isLoading}
          tab={tab}
          setTab={setTab}
          selectedApplication={selectedApplication}
          onApplicationSelect={navigateToApplication}
          hasTokens={dream_job_tokens > 0}
          search={debouncedSearch}
          setSearch={setDebouncedSearch}
        />
      )}
      {showSelectedApplication && (
        <div className="selected-application-pane">
          {selectedApplication && (
            <SelectedApplication
              application={selectedApplication}
              hasTokens={dream_job_tokens > 0}
              onViewParentApplication={onViewParentApplication}
            />
          )}
          {!isLoading && !selectedApplication && (
            <EmptyState svg={Fingerprint1} title={t("no_active")} message={t("know_whats_next")} />
          )}
        </div>
      )}
    </>
  );
};

const centeredState = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
};

const ErrorState = () => {
  return (
    <div style={centeredState}>
      <Error header={"Failed to fetch applications"} />
    </div>
  );
};

const LoadingState = () => {
  return (
    <div style={centeredState}>
      <LoadingSpinner size="medium" />
    </div>
  );
};

const ApplicationsPage = (props: Props) => {
  const device = useDevice();

  const goBack = () => {
    const route = "/applications";
    router.visit(route, { only: ["selected_application_id"] });
  };

  return (
    <main className="applications-page">
      {!!props.selected_application_id && device.type == "mobile" && (
        <IconButton
          className="back-button"
          icon={BackIcon}
          size="md"
          label="Back"
          onClick={goBack}
        />
      )}
      <Row gap="24px">
        <ErrorBoundary fallback={<ErrorState />}>
          <Suspense fallback={<LoadingState />}>
            <Applications {...props} />
          </Suspense>
        </ErrorBoundary>
      </Row>
    </main>
  );
};

export default ApplicationsPage;
